import { Button, Form, Input, Modal, Tabs, message } from 'antd'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getThesisPlan } from '@/constants/thesisPlans'
import { formatQuotaWan, useSubscriptionStore } from '@/stores/subscriptionStore'
import { isThesisCloudAuth, sendRegisterCode } from '@/lib/thesisApi'

/** 云端：邮箱验证码注册 + 密码登录；无 API 时退回本机演示 */
const AuthModal = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const open = searchParams.get('modal[auth]') === '1'
  const register = useSubscriptionStore((s) => s.register)
  const login = useSubscriptionStore((s) => s.login)
  const [tab, setTab] = useState<'login' | 'register'>('register')
  const [sending, setSending] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [busy, setBusy] = useState(false)
  const [loginForm] = Form.useForm<{ email: string; password: string }>()
  const [regForm] = Form.useForm<{
    email: string
    emailCode: string
    password: string
    displayName?: string
  }>()

  const cloud = isThesisCloudAuth()
  const gift = getThesisPlan('trial')

  const close = () => {
    searchParams.delete('modal[auth]')
    setSearchParams(searchParams)
    loginForm.resetFields()
    regForm.resetFields()
  }

  const tickCooldown = (sec: number) => {
    setCooldown(sec)
    const t = window.setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          window.clearInterval(t)
          return 0
        }
        return c - 1
      })
    }, 1000)
  }

  const onSendCode = async () => {
    const email = regForm.getFieldValue('email')
    try {
      await regForm.validateFields(['email'])
    } catch {
      return
    }
    setSending(true)
    try {
      const r = await sendRegisterCode(email)
      if (!r.ok) {
        message.error(r.error)
        return
      }
      message.success(r.data.devLogged ? '验证码已打印在服务端日志（开发模式）' : '验证码已发送，请查收邮箱')
      tickCooldown(r.data.cooldownSec || 60)
    } finally {
      setSending(false)
    }
  }

  const onLogin = async () => {
    const values = await loginForm.validateFields()
    setBusy(true)
    try {
      await login(values)
      message.success('已登录')
      close()
    } catch (e) {
      message.error(e instanceof Error ? e.message : '登录失败')
    } finally {
      setBusy(false)
    }
  }

  const onRegister = async () => {
    const values = await regForm.validateFields()
    setBusy(true)
    try {
      await register(values)
      message.success(
        `注册成功，赠送约 ${formatQuotaWan(gift.quotaWan * 10_000)} Token（${gift.days} 天内有效）`,
      )
      close()
    } catch (e) {
      message.error(e instanceof Error ? e.message : '注册失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="登录 / 注册"
      open={open}
      onCancel={close}
      footer={null}
      width={440}
      destroyOnClose
    >
      <p className="thesis-muted mb-4 text-sm">
        注册即送约 {gift.quotaWan} 万 Token 体验额度，用完可开通套餐。
      </p>

      {!cloud ? (
        <Form
          form={regForm}
          layout="vertical"
          onFinish={() =>
            register({
              email: regForm.getFieldValue('email'),
              displayName: regForm.getFieldValue('displayName'),
            }).then(() => {
              message.success('已登录（本机演示）')
              close()
            })
          }
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请填写邮箱' },
              { type: 'email', message: '邮箱格式不对' },
            ]}
          >
            <Input placeholder="name@example.com" autoComplete="email" size="large" />
          </Form.Item>
          <Form.Item name="displayName" label="称呼（可选）">
            <Input placeholder="怎么称呼你" autoComplete="nickname" size="large" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block size="large">
            登录并领取赠送额度
          </Button>
        </Form>
      ) : (
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as 'login' | 'register')}
          items={[
            {
              key: 'register',
              label: '注册',
              children: (
                <Form form={regForm} layout="vertical" onFinish={onRegister} requiredMark={false}>
                  <Form.Item
                    name="email"
                    label="邮箱"
                    rules={[
                      { required: true, message: '请填写邮箱' },
                      { type: 'email', message: '邮箱格式不对' },
                    ]}
                  >
                    <Input placeholder="name@example.com" autoComplete="email" size="large" />
                  </Form.Item>
                  <Form.Item label="邮箱验证码" required>
                    <div className="flex gap-2">
                      <Form.Item
                        name="emailCode"
                        noStyle
                        rules={[
                          { required: true, message: '请填写验证码' },
                          { pattern: /^\d{6}$/, message: '6 位数字' },
                        ]}
                      >
                        <Input placeholder="6 位验证码" size="large" maxLength={6} />
                      </Form.Item>
                      <Button
                        size="large"
                        disabled={cooldown > 0}
                        loading={sending}
                        onClick={onSendCode}
                      >
                        {cooldown > 0 ? `${cooldown}s` : '获取验证码'}
                      </Button>
                    </div>
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label="密码"
                    rules={[
                      { required: true, message: '请设置密码' },
                      { min: 8, message: '至少 8 位' },
                    ]}
                  >
                    <Input.Password autoComplete="new-password" size="large" />
                  </Form.Item>
                  <Form.Item name="displayName" label="称呼（可选）">
                    <Input placeholder="怎么称呼你" autoComplete="nickname" size="large" />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" block size="large" loading={busy}>
                    注册并领取赠送额度
                  </Button>
                </Form>
              ),
            },
            {
              key: 'login',
              label: '登录',
              children: (
                <Form form={loginForm} layout="vertical" onFinish={onLogin} requiredMark={false}>
                  <Form.Item
                    name="email"
                    label="邮箱"
                    rules={[
                      { required: true, message: '请填写邮箱' },
                      { type: 'email', message: '邮箱格式不对' },
                    ]}
                  >
                    <Input placeholder="name@example.com" autoComplete="email" size="large" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label="密码"
                    rules={[{ required: true, message: '请填写密码' }]}
                  >
                    <Input.Password autoComplete="current-password" size="large" />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" block size="large" loading={busy}>
                    登录
                  </Button>
                </Form>
              ),
            },
          ]}
        />
      )}
    </Modal>
  )
}

export default AuthModal
